import { DirectionRight, PromptQuestionCircleFill } from "@/Icons";
import {
  CrowdDistriItem,
  DescObjectType,
  QueryCrowdDistriResult,
  QueryFcpaTimeRangeResult,
} from "@/api/business_insight/interface";
import { DatePicker, Select, Spin, Toast, Tooltip } from "@ad/anq-ui";
import GlobalContext from "models/globalContext";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { queryFcpaTimeRange, queryCrowdDistri } from "src/api/business_insight";
import moment from "moment";
import { cardData } from "views/menu_layout/crowd_distribute/components/ItemCard/config";
import { numberChange } from "@/utils/numberChange";
import { ResourceCodeEnums } from "@/api/dmp_common/interface";
import { useAuth } from "@/common/hooks/use_auth";
import { useLoadingFn } from "@afe/rocket-hooks";

/** 首页人群资产模块 */
const Crowd = () => {
  const { brandInfoList } = useContext(GlobalContext);
  const [hasAuth] = useAuth(ResourceCodeEnums.CROWD_TRANS);

  const [canSelectTimeRange, setCanSelectTimeRange] =
    useState<QueryFcpaTimeRangeResult>();
  const [brandId, setBrandId] = useState<number>(brandInfoList[0]?.brandId);
  const [fcpaPt, setFcpaPt] = useState<string>();
  const [fcpaDistribute, setFcpaDistribute] =
    useState<QueryCrowdDistriResult>();

  const descObject: DescObjectType = useMemo(() => {
    try {
      if (fcpaDistribute?.desc) {
        return JSON.parse(fcpaDistribute.desc);
      }

      return {};
    } catch (error) {
      return {};
    }
  }, [fcpaDistribute]);

  useEffect(() => {
    if (hasAuth) {
      getTimeRange();
    }
  }, [brandId]);

  useEffect(() => {
    getCrowdDistribute(fcpaPt);
  }, [fcpaPt]);

  const handleJump = () => {
    window.open(
      `/${ResourceCodeEnums.CROWD}?menu=${ResourceCodeEnums.CROWD_DISTRIBUTE}`
    );
  };

  const getTimeRange = async () => {
    const res = await queryFcpaTimeRange({ brandId, cate2Id: 0 });

    if (res?.success) {
      setCanSelectTimeRange(res.result);
      if (hasAuth) {
        getCrowdDistribute(res.result.endPt);
      }
    } else {
      Toast.error(res?.errorMsg || "查询可选时间失败");
    }
  };

  const [loading, getCrowdDistribute] = useLoadingFn(async (pt: string) => {
    const res = await queryCrowdDistri({
      brandId,
      cate2Id: 0,
      fcpaPt: pt,
    });

    if (res?.result) {
      setFcpaDistribute(res.result);
    } else {
      Toast.error(res?.errorMsg || "查询人群分布失败");
    }
  });

  return (
    <div className="mt-4 rounded-lg bg-white p-6">
      <div className="title mb-6 overflow-hidden">
        <span className="text-base font-semibold leading-8">人群资产</span>
        <div className="title-tools float-right flex">
          <Select
            className="mr-2 w-[240px]"
            value={brandId}
            onChange={(v) => setBrandId(v)}
          >
            {brandInfoList.map((item) => (
              <Select.Option key={item.brandId} value={item.brandId}>
                {item.brandName}
              </Select.Option>
            ))}
          </Select>
          <DatePicker
            value={moment(fcpaPt).isValid() ? moment(fcpaPt) : undefined}
            onChange={(v) => setFcpaPt(v.format("YYYY-MM-DD"))}
            disabledDate={(v) =>
              v.isAfter(moment(canSelectTimeRange?.endPt)) ||
              v.isBefore(moment(canSelectTimeRange?.startPt))
            }
            allowClear={false}
          />
          <span
            className="dmp-link ml-4 flex h-8 cursor-pointer items-center text-text-black/[.65]"
            onClick={handleJump}
          >
            查看更多
            <DirectionRight />
          </span>
        </div>
      </div>

      <Spin spinning={loading}>
        <div className="flex gap-4">
          {cardData.map((item) => {
            const currentInfo: CrowdDistriItem =
              fcpaDistribute?.[item.key.slice(0, 1)] || {};

            return (
              <div
                key={item.key}
                className="flex-1 rounded-lg bg-[#FAFAFC] p-4"
              >
                <div className="title mb-3 flex items-center text-sm font-semibold">
                  <img src={item.icon} className="mr-1 h-6 w-6" />
                  {item.name}
                  <Tooltip title={descObject[item.key]?.tips}>
                    <PromptQuestionCircleFill className="ml-1 cursor-pointer text-question" />
                  </Tooltip>
                </div>
                <div className="mb-4 text-lg font-semibold">
                  {numberChange(currentInfo.currentCount)}
                </div>
                <div className="flex">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <span className="text-xs text-text-black/[.65]">
                        当日流入
                      </span>
                      <Tooltip title={descObject.todayIncrement}>
                        <PromptQuestionCircleFill className="ml-[2px] cursor-pointer text-question" />
                      </Tooltip>
                    </div>
                    <div className="mt-1 text-sm font-semibold">
                      {numberChange(currentInfo.incrementCount)}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center">
                      <span className="text-xs text-text-black/[.65]">
                        当日流出
                      </span>
                      <Tooltip title={descObject.todayLoss}>
                        <PromptQuestionCircleFill className="ml-[2px] cursor-pointer text-question" />
                      </Tooltip>
                    </div>
                    <div className="mt-1 text-sm font-semibold">
                      {numberChange(currentInfo.lossCount)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Spin>
    </div>
  );
};

export default Crowd;
